import React, { useEffect, useState } from "react";
import {
  FormDefinition,
  QuestionnaireItem as Question,
  WizardStep as Step,
  generateUniqueId,
} from "../../../../interfaces/dynamicForms/FormBuilder";
import FormInput from "./FormInput";
import QuestionEditor from "./QuestionEditor";
import {
  faUser,
  faMapLocationDot,
  faAddressBook,
  faBolt,
  faFolderOpen,
  faClipboardQuestion,
  faSignature,
  faFileInvoiceDollar,
  faHome,
  faReceipt,
  faCalculator,
  faBars,
  IconDefinition,
  faArrowDown,
  faArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "react-bootstrap";
import { getIcon } from "../DynamicFormComponents/utils";
import "./FormBuilder.css";


interface StepEditorProps {
  step: Step;
  formDefinition: FormDefinition;
  updateStep: (stepId: string, updatedStep: Partial<Step>) => void;
  deleteStep: (stepId: string) => void;
  addQuestion: (stepId: string, newQuestion: Question) => void;
  updateQuestion: (
    stepId: string,
    questionId: string,
    updatedQuestion: Question
  ) => void;
  deleteQuestion: (stepId: string, questionId: string) => void;
  isFirstStep: boolean;
  stepOrder: number;
  isLastStep: boolean;
  reorderStep: (stepId: string, direction: "up" | "down") => void;
  reorderQuestion: (
    stepId: string,
    questionId: string,
    direction: "up" | "down"
  ) => void;
  moveQuestionAcrossSteps: (
    fromStepId: string,
    questionId: string,
    toStepId: string
  ) => void;
  totalSteps: number;
}

const StepEditor: React.FC<StepEditorProps> = ({
  step,
  formDefinition,
  updateStep,
  deleteStep,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  isFirstStep,
  isLastStep,
  reorderStep,
  reorderQuestion,
  stepOrder,
  moveQuestionAcrossSteps,
  totalSteps,
}) => {
  const fontAwesomeIconOptions: {
    value: string;
    icon: IconDefinition;
    label: string;
  }[] = [
    { value: "faUser", icon: faUser, label: "User" },
    { value: "faMapLocationDot", icon: faMapLocationDot, label: "Map Marker" },
    { value: "faAddressBook", icon: faAddressBook, label: "Address Book" },
    { value: "faBolt", icon: faBolt, label: "Bolt" },
    { value: "faFolderOpen", icon: faFolderOpen, label: "Open Folder" },
    {
      value: "faClipboardQuestion",
      icon: faClipboardQuestion,
      label: "Question Clipboard",
    },
    { value: "faSignature", icon: faSignature, label: "Signature" },
    {
      value: "faFileInvoiceDollar",
      icon: faFileInvoiceDollar,
      label: "Invoice Dollar",
    },
    { value: "faHome", icon: faHome, label: "Home" },
    { value: "faReceipt", icon: faReceipt, label: "Receipt" },
    { value: "faCalculator", icon: faCalculator, label: "Calculator" },
    { value: "faBars", icon: faBars, label: "Bars" },
  ];

  const [localStepId, setLocalStepId] = useState(step.id);

  useEffect(() => {
    setLocalStepId(step.id);
  }, [step.id]);
  const handleAddQuestion = () => {
    const newQuestion: Question = {
      id: generateUniqueId(`q-${step.id}`),
      label: "New Question",
      type: "text",
    };
    addQuestion(step.id, newQuestion);
  };

  const handleStepChange = (field: keyof Step, value: string) => {
    updateStep(step.id, { [field]: value });
  };

  return (
    <>
      <div className="form_cerate_step">
        <div className="top top_bar_flx border_tittle mby_16">
          <div className="top_bar_tittle sm_tittle">
            <h2>{`Step ${stepOrder + 1}: `} {step.id}</h2>
          </div>
          <div className="top_right_flx">
            <button
              onClick={() => reorderStep(step.id, "up")}
              disabled={isFirstStep}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faArrowUp} />
            </button>
            <button
              onClick={() => reorderStep(step.id, "down")}
              disabled={isLastStep}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faArrowDown} />
            </button>
            <Button variant="danger" className="btn_sm" onClick={() => deleteStep(step.id)} >Delete Step</Button>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-4 labelBlock">
            <div className="custom_input_flled">
                <FormInput
                  label="Step ID"
                  id={`step-${step.id}-id`}
                  value={localStepId}
                  onChange={(e) => setLocalStepId(e.target.value)}
                  onBlur={() => updateStep(step.id, { id: localStepId })}
                  required
                />
                <div className="stats_filled mt-1">
                  <p>
                    A unique internal ID for this step. It's used for navigation and
                    logic, so it's best not to change it after it's been set.
                  </p>
                </div>
              </div>
          </div>
          <div className="col-xl-4 labelBlock">
            <div className="custom_input_flled">
              <FormInput
                label="Step Label"
                id={`step-${step.id}-label`}
                value={step.label}
                onChange={(e) => handleStepChange("label", e.target.value)}
                required
              />
              <div className="stats_filled mt-1">
                <p>
                  The title of this form step that will be visible to the user (e.g.,
                  'Personal Information').
                </p>
              </div>
            </div>
          </div>
          <div className="col-xl-4 labelBlock">
            <div className="custom_input_flled">
              <label
                htmlFor={`step-${step.id}-icon`}
                className=""
              >
                Icon
              </label>
              <select
              style={{maxWidth: 'unset'}}
                id={`step-${step.id}-icon`}
                value={step.icon || "faBars"}
                onChange={(e) => handleStepChange("icon", e.target.value)}
                className="sm_input filled_white"
              >
                {fontAwesomeIconOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="stats_filled mt-1">
                <p>
                  A visual icon to represent this step, often shown in a progress bar.
                  Current: <FontAwesomeIcon icon={getIcon(step.icon || "faBars")} />
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="stats_filled">
          <label>Questions ({step.questions.length})</label>
          <p>All the questions that will appear on this page of the form are listedbelow.</p>
        </div>
        <div className="pl-4 border-l-4 border-green-200" style={{borderColor: 'var(--primary-color)'}}>
          {step.questions.map((question, qi) => (
            <QuestionEditor
              key={question.id}
              question={question}
              stepId={step.id}
              formDefinition={formDefinition}
              updateQuestion={updateQuestion}
              deleteQuestion={deleteQuestion}
              reorderQuestion={reorderQuestion}
              moveQuestionAcrossSteps={moveQuestionAcrossSteps}
              isFirst={qi === 0}
              isLast={qi === step.questions.length - 1}
              order={qi}
              stepIndex={stepOrder}
              totalSteps={totalSteps}
            />
          ))}
        </div>
        <div className="mby_t_24">
          <Button variant="primary" className="btn_sm" onClick={handleAddQuestion}>Add Question to Step</Button>
        </div>
      </div>
    </>
  );
};

export default StepEditor;
